'use client';

/**
 * PinHeaderNav — the mobile pinned-card's in-header prev/dots/next cluster
 * (design turn 14a). Lives in the shell's header row (via
 * PinnedCardShell's `headerNav` slot) rather than below the card the way
 * desktop's CardStack renders its arrows+dots — the compact ~150px mobile
 * anatomy has no room for a second row underneath. Purely presentational:
 * `index`/`count` are owned by the caller (SwipeTrack shares the same
 * `index` state) so arrow taps and finger-drag commits can never disagree
 * about position.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PinHeaderNavProps {
  index: number;
  count: number;
  onPrev: () => void;
  onNext: () => void;
  className?: string;
}

export function PinHeaderNav({
  index,
  count,
  onPrev,
  onNext,
  className,
}: PinHeaderNavProps) {
  const atStart = index === 0;
  const atEnd = index === count - 1;

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <button
        type="button"
        onClick={onPrev}
        disabled={atStart}
        aria-label="Previous card"
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded-full transition-colors',
          atStart ? 'text-[#e0cdb4]' : 'text-[#E36001]'
        )}
      >
        <ChevronLeft className="size-[13px]" strokeWidth={2.5} />
      </button>

      <div className="flex items-center gap-1">
        {Array.from({ length: count }, (_, i) => (
          <span
            key={i}
            data-testid="pin-header-nav-dot"
            data-active={i === index}
            className={cn(
              'rounded-[2.5px] transition-all',
              i === index
                ? 'h-[5px] w-4 bg-[#E36001]'
                : 'size-[5px] bg-[#e0cdb4]'
            )}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={onNext}
        disabled={atEnd}
        aria-label="Next card"
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded-full transition-colors',
          atEnd ? 'text-[#e0cdb4]' : 'text-[#E36001]'
        )}
      >
        <ChevronRight className="size-[13px]" strokeWidth={2.5} />
      </button>
    </div>
  );
}
